import { ChangeEvent, useState } from 'react';
import { RangeSlider } from '../../../../../Common/RangeSlider';
import { addCommasToNumber } from '../../../../../Utils/CommonUtils';

interface dropdownType {
  value: string;
  label: string;
  id: string;
}
interface CalcInputProps {
  warning?: string;
  warningText?: string;
  label_text: string;
  min_value?: number;
  max_value?: number;
  default_value?: number;
  step_value?: number;
  getValue?: (value: number, dropdownValue: string) => void;
  prefix?: string;
  suffix?: string;
  dropdownValues?: Array<dropdownType> | [{ value: ''; label: ''; id: '' }];
  arabic?: boolean;
  addCommas?: boolean;
  style?: {
    textInput_outerdiv?: string;
    textInput_label?: string;
    component_outerdiv?: string;
  };
  hidden?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

const InputRangeSlider = ({
  warning,
  warningText,
  label_text,
  min_value = 0,
  max_value = 100,
  default_value = 1,
  step_value = 1,
  prefix,
  suffix,
  addCommas = true,
  style = {
    textInput_outerdiv: '',
    textInput_label: 'text-base font-medium',
    component_outerdiv: 'py-2 sm:py-1',
  },
  hidden = false,
  onChange,
}: CalcInputProps): JSX.Element => {
  const [inputVal, setInputValue] = useState<number>(default_value);
  const [minMaxError, setMinMaxError] = useState<string>('');

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const amt = parseFloat(value.replace(/,/g, '')) || 0;
    const amtOrMax = amt > max_value ? max_value : amt;
    setInputValue(amtOrMax);

    if (
      (min_value !== undefined && amt < min_value) ||
      (max_value !== undefined && amt > max_value)
    ) {
      setMinMaxError(`Value must be between ${min_value} and ${max_value}`);
    } else {
      setMinMaxError('');
    }
    onChange && onChange(e);
  };

  return (
    <div className={`${style.component_outerdiv} ${hidden ? 'hidden' : ''}`}>
      <label htmlFor={`input_${label_text}`} className={style.textInput_label}>
        {label_text}
      </label>
      <div className="flex gap-4 sm:gap-3 sm:items-center">
        <div className="w-9/12 sm:w-3/5"> 
          <RangeSlider
            className="my-5 sm:my-3"
            min_value={min_value}
            max_value={max_value}
            input_value={inputVal}
            step_value={step_value}
            handleChange={handleAmountChange}
          />
        </div>
        <div className={'relative w-3/12 sm:w-[38%] ' + style.textInput_outerdiv}>
          <input
            className="border rounded border-grey-300 h-12 sm:h-9 focus:outline-none px-4 sm:px-2 py-1 w-full text-base font-medium"
            type="number"
            value={addCommas ? addCommasToNumber(inputVal) : inputVal}
            onChange={handleAmountChange}
            id={`input_${label_text}`}
            name={`input_${label_text}`}
          />
          {prefix && (
            <span className="absolute top-1 sm:top-0.5 left-1.5 sm:left-1 bg-white py-2 sm:py-1.5 h-10 sm:h-8 text-base font-medium">
              {prefix}
            </span>
          )}
          {suffix && (
            <span className="absolute top-1 sm:top-0.5 right-1.5 sm:right-1 bg-white py-2 sm:py-1.5 h-10 sm:h-8 text-base font-medium">
              {suffix}
            </span>
          )}
        </div>
      </div>
      {minMaxError ? (
        <p className="text-red-350 text-s-14 font-medium inline-block">
          {minMaxError}
        </p>
      ) : null}
      {warning === 'yes' && minMaxError === '' ? (
        <p className="text-red-350 text-s-14 font-medium inline-block">
          {warningText}
        </p>
      ) : null}
    </div>
  );
};

export default InputRangeSlider;
